/**
 * Food & Beverage — the shared vocabulary for restaurants, bars, cafés and
 * takeaways on the platform.
 *
 * F&B is NOT a fourth storefront domain: a food brand keeps a primary
 * industry (fashion/electronics — that decides the host) and opts into the
 * food business type. What changes is the product template (a MENU ITEM:
 * portions instead of sizes, made-to-order instead of stock), the till
 * (dine in / takeaway / delivery + table), the kitchen board, and the
 * wording on the storefront and receipts.
 *
 * Plain TypeScript (one type import from the business-kind registry) —
 * imported by both the React app and Convex (`convex/foodBeverage/*` reach it
 * via a relative path).
 */

import type { BusinessKind } from "./businessKinds";

export const FOOD_INDUSTRY_SLUG = "food-beverage" as const;

export type BusinessType = BusinessKind;

export type ServiceMode = "dine_in" | "takeaway" | "delivery";

export type KitchenStatus = "new" | "preparing" | "ready" | "served";

export type FoodGroup = "food" | "drink";

export interface FoodPortion {
  name: string;
  price: number;
  /**
   * Pack portions (the bar): how many base units this portion is — a bottle
   * is 1, a crate of 24 is 24. When any portion of an item carries units,
   * ALL its portions draw from ONE stock held in base units (isPackPortions).
   */
  units?: number;
  /**
   * What THIS portion costs the house, when it differs from the item's cost:
   * a glass of wine against the bottle, a crate at the distributor's price.
   * Absent = the item's cost (× units for a pack portion).
   */
  costPrice?: number;
}

/** One answer to an option group. `price` is EXTRA, on top of the portion. */
export interface FoodOptionChoice {
  name: string;
  /** Added to the line's unit price. Absent or 0 = costs nothing. */
  price?: number;
}

/**
 * A question the counter answers before the item joins the cart — how it is
 * cooked, what goes with it, which extras. Grilled chicken is not one dish:
 * "chilli" and "plain" are different plates to the kitchen, and only some of
 * them change the price.
 */
export interface FoodOptionGroup {
  name: string;
  /** The till refuses to add the line until this one is answered. */
  required?: boolean;
  /** More than one choice may be picked (extras, toppings). */
  multiple?: boolean;
  choices: FoodOptionChoice[];
}

/** What the counter actually picked, carried on the cart and order line. */
export interface SelectedOption {
  group: string;
  choice: string;
  /** The extra charged for this choice at the time of sale. */
  price?: number;
}

export interface FoodDetails {
  /** Portion sizes with their own prices (Regular / Large, Glass / Jug…). */
  portions?: FoodPortion[];
  /** Questions the counter answers per line — see FoodOptionGroup. */
  optionGroups?: FoodOptionGroup[];
  dietaryTags?: string[];
  /** 0 = not spicy … 3 = hot. */
  spiceLevel?: number;
  prepMinutes?: number;
  /** false = taken off today's menu without deleting it. */
  available?: boolean;
  group?: FoodGroup;
  /**
   * Prepared to order by the kitchen/bar → the order joins the kitchen
   * board. Absent = food yes, drinks no. Bottled drinks and packaged goods
   * are handed over at the counter and never create a ticket.
   */
  needsPreparation?: boolean;
  /**
   * The counter that makes it — kitchen, bar, shisha, grill… (STATIONS).
   * Absent: drinks → bar, food → kitchen (stationOf). The Kitchen board
   * shows a tab per station, so the shisha counter sees only its pipes.
   */
  station?: string;
}

export const SERVICE_MODES: ReadonlyArray<{ value: ServiceMode; label: string; hint: string }> = [
  { value: "dine_in", label: "Dine in", hint: "Eating here — add the table" },
  { value: "takeaway", label: "Takeaway", hint: "Packed to go" },
  { value: "delivery", label: "Delivery", hint: "Sent to an address" },
];

export const KITCHEN_FLOW: ReadonlyArray<{
  value: KitchenStatus;
  label: string;
  next?: KitchenStatus;
  action?: string;
}> = [
  { value: "new", label: "New", next: "preparing", action: "Start preparing" },
  { value: "preparing", label: "Preparing", next: "ready", action: "Mark ready" },
  { value: "ready", label: "Ready", next: "served", action: "Served" },
  { value: "served", label: "Served" },
];

/** The counters an order can be made at. The Kitchen board shows a tab per station. */
export const STATIONS: ReadonlyArray<{ value: string; label: string; icon: string; hint: string }> = [
  { value: "kitchen", label: "Kitchen", icon: "🍳", hint: "Plates, sauces, the pass" },
  { value: "bar", label: "Bar", icon: "🍺", hint: "Drinks poured, mixed or opened" },
  { value: "shisha", label: "Shisha", icon: "💨", hint: "Pipes, heads and coals" },
  { value: "grill", label: "Grill", icon: "🔥", hint: "Muchomo and the barbecue outside" },
  { value: "pastry", label: "Pastry", icon: "🎂", hint: "Cakes and desserts" },
];

export function stationLabel(key: string | undefined | null): string {
  const k = String(key || "kitchen");
  return STATIONS.find((s) => s.value === k)?.label || k.charAt(0).toUpperCase() + k.slice(1);
}

/** Which counter makes a menu item: its own station, else drinks → bar, food → kitchen. */
export function stationOf(product: any): string {
  const own = product?.food?.station;
  if (typeof own === "string" && own.trim()) return own.trim();
  return product?.food?.group === "drink" ? "bar" : "kitchen";
}

export const FOOD_DIETARY_TAGS: ReadonlyArray<string> = [
  "Vegetarian",
  "Vegan",
  "Halal",
  "Gluten-free",
  "Dairy-free",
  "Contains nuts",
  "Chef's special",
];

export const FOOD_SPICE_LEVELS: ReadonlyArray<{ value: number; label: string }> = [
  { value: 0, label: "Not spicy" },
  { value: 1, label: "Mild" },
  { value: 2, label: "Medium" },
  { value: 3, label: "Hot" },
];

/** Generic artwork for a menu item with no photo yet. */
export const FOOD_PLACEHOLDER_IMAGE = "/images/placeholder-product.jpg";

/**
 * Platform-level categories for the industry (categories are per industry,
 * not per brand — same as fashion and electronics). Seeded once by
 * `foodBeverage/seed:seedFoodCategories`; a brand only sees the ones its
 * menu actually uses.
 */
export const FOOD_CATEGORY_SEED: ReadonlyArray<{
  name: string;
  slug: string;
  group: FoodGroup | "other";
  icon: string;
  description: string;
}> = [
  { name: "Starters & Snacks", slug: "starters-snacks", group: "food", icon: "🥟", description: "Samosas, wings, bites and small plates" },
  { name: "Main Dishes", slug: "main-dishes", group: "food", icon: "🍛", description: "Plated mains — stews, rice dishes, pasta" },
  { name: "Local Dishes", slug: "local-dishes", group: "food", icon: "🍲", description: "Luwombo, matooke, posho, katogo and other home favourites" },
  { name: "Grills & Barbecue", slug: "grills-barbecue", group: "food", icon: "🍢", description: "Muchomo, nyama choma, grilled fish and chicken" },
  { name: "Fast Food", slug: "fast-food", group: "food", icon: "🍔", description: "Rolex, chips, burgers, wraps" },
  { name: "Pizza", slug: "pizza", group: "food", icon: "🍕", description: "Pizzas by size" },
  { name: "Breakfast", slug: "breakfast", group: "food", icon: "🍳", description: "Morning plates, eggs, pancakes" },
  { name: "Salads & Sides", slug: "salads-sides", group: "food", icon: "🥗", description: "Salads, chips, rice and other sides" },
  { name: "Soft Drinks & Juices", slug: "soft-drinks-juices", group: "drink", icon: "🧃", description: "Sodas, fresh juices, smoothies" },
  { name: "Hot Drinks", slug: "hot-drinks", group: "drink", icon: "☕", description: "Coffee, tea, chai, hot chocolate" },
  { name: "Beers & Ciders", slug: "beers-ciders", group: "drink", icon: "🍺", description: "Bottled and draught" },
  { name: "Wines", slug: "wines", group: "drink", icon: "🍷", description: "By the glass or bottle" },
  { name: "Spirits", slug: "spirits", group: "drink", icon: "🥃", description: "Tots and bottles" },
  { name: "Cocktails", slug: "cocktails", group: "drink", icon: "🍹", description: "Mixed drinks and mocktails" },
  { name: "Water", slug: "water", group: "drink", icon: "💧", description: "Still and sparkling" },
  { name: "Shisha & Hookah", slug: "shisha-hookah", group: "drink", icon: "💨", description: "Shisha by the pipe, in flavours" },
  { name: "Desserts & Bakery", slug: "desserts-bakery", group: "other", icon: "🍰", description: "Cakes, pastries, ice cream" },
  { name: "Platters & Combos", slug: "platters-combos", group: "other", icon: "🍽️", description: "Sharing platters and meal deals" },
  { name: "Kids Menu", slug: "kids-menu", group: "other", icon: "🧒", description: "Smaller portions for children" },
];

/** A product that renders and sells as a menu item. */
export function isFoodProduct(product: any): boolean {
  if (!product) return false;
  return product.industry === FOOD_INDUSTRY_SLUG || !!product.food;
}

/** Made-to-order items never run "out of stock"; bottled goods do. */
export function isStockTracked(product: any): boolean {
  return product?.trackStock !== false;
}

export function isFoodAvailable(product: any): boolean {
  return product?.food?.available !== false;
}

export type MenuWebsiteState = "orderable" | "sold_out" | "off_menu" | "not_online";

/**
 * Can a customer order this menu item on the website — and if not, WHY, in
 * the owner's words. One rule for the menu form's "On your website" line, so
 * "Sold out" on the website is never a mystery: no stock, taken off the menu
 * for today, or kept at the till only (TAVOLA KAMPALA, 29 Oct 2026: a drink
 * with 50 at the till read sold out online and nobody could say why).
 *
 * `stock` is the shelf count for a tracked item (null/undefined = not known
 * yet; never treated as sold out).
 */
export function menuWebsiteState(input: {
  onWebsite: boolean;
  available: boolean;
  tracked: boolean;
  stock?: number | null;
}): { state: MenuWebsiteState; label: string; why: string } {
  if (!input.onWebsite) {
    return {
      state: "not_online",
      label: "Not on your website",
      why: "Sold at the till only. Switch on On your website to show it to customers.",
    };
  }
  if (!input.available) {
    return {
      state: "off_menu",
      label: "Off menu today",
      why: "Customers see it but cannot order it while Available today is off.",
    };
  }
  const known = typeof input.stock === "number" && Number.isFinite(input.stock);
  if (input.tracked && known && (input.stock as number) <= 0) {
    return {
      state: "sold_out",
      label: "Sold out",
      why: "Nothing in stock. It can be ordered again as soon as stock is received.",
    };
  }
  return {
    state: "orderable",
    label: "Can be ordered",
    why: !input.tracked
      ? "Made to order, so it never sells out."
      : known
        ? `${Math.floor(input.stock as number).toLocaleString()} in stock.`
        : "Stock is counted; it shows Sold out when it runs out.",
  };
}

/**
 * Does the kitchen (or bar) make this to order? Drives kitchen intake:
 * an order with at least one such line gets a ticket; an order made only of
 * ready items (bottled drinks, packaged goods) completes at the counter.
 */
export function needsKitchenPrep(product: any): boolean {
  if (!isFoodProduct(product)) return false;
  const food = product?.food || {};
  if (typeof food.needsPreparation === "boolean") return food.needsPreparation;
  return food.group !== "drink";
}

// Mirrors `sanitizeVariantId` in lib/utils/variantPricing.ts (kept inline so
// this file stays import-free for Convex).
function sanitizeId(id: string): string {
  // eslint-disable-next-line no-control-regex
  return id.replace(/[^\x20-\x7E]/g, "-").replace(/-{2,}/g, "-");
}

/**
 * The variant id a portion is stored under. Portions are ordinary product
 * variants (colour "std", size = portion name) so cart lines, stock keys,
 * receipts and refunds all work unchanged: `variant-std-<portion>`.
 */
export function portionVariantId(portionName: string): string {
  return sanitizeId(["variant", "std", portionName].join("-"));
}

/** Portions for a menu item — from `food.portions`, else from its variants. */
export function foodPortions(product: any): FoodPortion[] {
  const own = product?.food?.portions;
  if (Array.isArray(own) && own.length > 0) {
    return own.filter((p: any) => p && p.name).map((p: any) => ({
      name: String(p.name),
      price: typeof p.price === "number" && p.price > 0 ? p.price : Number(product?.price) || 0,
      ...(Number(p.units) > 1 ? { units: Math.round(Number(p.units)) } : {}),
      ...(Number(p.costPrice) > 0 ? { costPrice: Math.round(Number(p.costPrice)) } : {}),
    }));
  }
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const out: FoodPortion[] = [];
  for (const v of variants) {
    const size = v?.attributes?.size ?? v?.size;
    if (!size || size === "default") continue;
    out.push({
      name: String(size),
      price: typeof v.price === "number" && v.price > 0 ? v.price : Number(product?.price) || 0,
    });
  }
  return out;
}

/** Option groups for a menu item, cleaned of half-typed rows. */
export function foodOptionGroups(product: any): FoodOptionGroup[] {
  const raw = product?.food?.optionGroups;
  if (!Array.isArray(raw)) return [];
  const out: FoodOptionGroup[] = [];
  for (const g of raw) {
    const name = String(g?.name ?? "").trim();
    const choices = Array.isArray(g?.choices)
      ? g.choices
          .filter((c: any) => String(c?.name ?? "").trim())
          .map((c: any) => {
            const price = Number(c?.price);
            return { name: String(c.name).trim(), ...(price > 0 ? { price } : {}) };
          })
      : [];
    if (!name || choices.length === 0) continue;
    out.push({ name, required: !!g?.required, multiple: !!g?.multiple, choices });
  }
  return out;
}

/** What the chosen options add to the unit price. */
export function optionsExtra(selected: SelectedOption[] | undefined | null): number {
  if (!Array.isArray(selected)) return 0;
  return selected.reduce((sum, o) => sum + (Number(o?.price) > 0 ? Number(o.price) : 0), 0);
}

/** One line a human reads: "Medium rare · Extra chilli". */
export function optionsSummary(selected: SelectedOption[] | undefined | null): string {
  if (!Array.isArray(selected) || selected.length === 0) return "";
  return selected.map((o) => String(o?.choice ?? "").trim()).filter(Boolean).join(" · ");
}

/**
 * Two lines of the same dish with different answers are different lines.
 * The cart dedups on this, so "well done" never merges into "rare".
 */
export function optionsSignature(selected: SelectedOption[] | undefined | null): string {
  if (!Array.isArray(selected) || selected.length === 0) return "";
  return selected
    .map((o) => `${String(o?.group ?? "").trim()}=${String(o?.choice ?? "").trim()}`)
    .sort()
    .join("|");
}

/** Every required group answered? The till's Add button waits on this. */
export function optionsComplete(
  groups: FoodOptionGroup[],
  selected: SelectedOption[] | undefined | null,
): boolean {
  const picked = Array.isArray(selected) ? selected : [];
  return groups
    .filter((g) => g.required)
    .every((g) => picked.some((o) => o.group === g.name && String(o.choice || "").trim()));
}

export function foodPriceRange(product: any): { min: number; max: number } {
  const base = Number(product?.price) || 0;
  const portions = foodPortions(product);
  if (portions.length === 0) return { min: base, max: base };
  const prices = portions.map((p) => p.price);
  return { min: Math.min(...prices), max: Math.max(...prices) };
}

export function serviceModeLabel(mode?: string | null): string {
  const hit = SERVICE_MODES.find((m) => m.value === mode);
  return hit ? hit.label : "";
}

export function spiceLabel(level?: number | null): string {
  if (level === undefined || level === null) return "";
  return FOOD_SPICE_LEVELS.find((s) => s.value === level)?.label ?? "";
}

/**
 * Pack portions: a drink sold by the bottle, the carton and the crate, all
 * from ONE stock counted in bottles. True when any portion carries `units`.
 * Such an item keeps a single stock (no per-portion variants); a line's
 * stock draw is quantity × units, and the receipt reads "Crate of 24".
 */
export function isPackPortions(product: any): boolean {
  return foodPortions(product).some((p) => Number(p.units) > 1);
}

/** How many base units one of `size` is on this item (1 unless a pack portion). */
export function packUnitsFor(product: any, size: string | undefined | null): number {
  if (!product || !size || !isPackPortions(product)) return 1;
  const p = foodPortions(product).find((x) => x.name === size);
  return p && Number(p.units) > 1 ? Math.round(Number(p.units)) : 1;
}

/** The price of the named portion, or null when the item has no such portion. */
export function portionPrice(product: any, size: string | undefined | null): number | null {
  if (!product || !size || !isFoodProduct(product)) return null;
  const p = foodPortions(product).find((x) => x.name === size);
  return p ? p.price : null;
}

/**
 * What one of `size` costs the house: the portion's own cost when it has one,
 * else the item's cost — times the pack size for a crate or carton. Null when
 * nothing is known (no such portion and no item cost).
 */
export function portionCostPrice(product: any, size: string | undefined | null): number | null {
  if (!product) return null;
  const base = Number(product.costPrice);
  const portion = size ? foodPortions(product).find((p) => p.name === size) : undefined;
  if (portion && Number(portion.costPrice) > 0) return Math.round(Number(portion.costPrice));
  if (!Number.isFinite(base) || base <= 0) return null;
  return Math.round(base * packUnitsFor(product, size));
}

/**
 * Does this sale need "How is it served?" (dine in / takeaway / delivery)?
 *
 * A food BUSINESS always asks — a restaurant serves a soda at a table too.
 * Anyone else asks only when a line is a menu item the kitchen or bar
 * PREPARES: Fitness World added a bottled juice and every T-shirt sale
 * started offering Dine in / Takeaway / Delivery instead of Pick up /
 * Delivery, because the question was asked of the brand, not the cart.
 * `productOf` resolves a line's product (the till's catalogue), so a line
 * whose product is unknown counts as ready — the safe answer.
 */
export function saleAsksHowServed(opts: {
  isFoodBusiness: boolean;
  lines: ReadonlyArray<{ itemType?: string; productId?: string | null }>;
  productOf: (productId: string) => any;
}): boolean {
  if (opts.isFoodBusiness) return true;
  return opts.lines.some((line) => {
    if (line.itemType === "service" || line.itemType === "b2b" || !line.productId) return false;
    const product = opts.productOf(String(line.productId));
    return !!product && needsKitchenPrep(product);
  });
}

// ── Stock per size ──────────────────────────────────────────────────────────
//
// A menu item whose sizes are sold SINGLY (a Big and a Small bottle of juice)
// keeps a separate count for each size, under the size's variant id — the id
// the till puts on every line it sells (`portionVariantId`). The server
// matches a sale to its size strictly (convex/pos/stockAllocation.ts), so
// stock saved under any other key — above all the plain "default" a one-box
// stock screen writes — can never be sold. Fitness World's Fresh Juice read
// 12 on an empty shelf and refused every Small bottle for that reason: every
// stock screen offered one box and saved it to "default".
//
// Bottle / carton / crate items (isPackPortions) are NOT per size: every pack
// draws from ONE stock of bottles, which lives under "default" on purpose.

/** The key a plain item's one stock — and stock saved without a size — lives under. */
export const UNSIZED_VARIANT_ID = "default";

/** What staff read for stock saved without a size on an item sold by size. */
export const UNSIZED_STOCK_LABEL = "No size (can't be sold)";

export interface PortionStockRow {
  /** The id its batches carry and the till sells: `variant-std-<Name>`, exact casing. */
  variantId: string;
  /** The size's own name ("Big"), or UNSIZED_STOCK_LABEL. */
  label: string;
  /**
   * Stock saved WITHOUT a size. Listed only so it can be taken off — the
   * server refuses to add to it.
   */
  unsized?: boolean;
}

/**
 * The key a stock figure is filed under, compared the way the server compares
 * variant ids (trimmed, then the sanitising every variant id gets). A batch
 * with no variant id is unsized stock.
 */
export function stockVariantKey(variantId: string | null | undefined): string {
  const trimmed = String(variantId ?? "").trim();
  return trimmed ? sanitizeId(trimmed) : UNSIZED_VARIANT_ID;
}

/**
 * The stock rows for a menu item whose sizes are sold singly: one per size,
 * in menu order. Null for everything else — a pack item, a menu item without
 * sizes, anything that isn't a menu item — so the caller keeps its own rows
 * (fashion colour × size, electronics colour × storage, one plain box).
 *
 * `unsizedStock` > 0 appends the "No size (can't be sold)" row, so a screen
 * that corrects counts can clear stock that was saved without a size.
 */
export function portionStockRows(
  product: any,
  opts?: { unsizedStock?: number },
): PortionStockRow[] | null {
  if (!isFoodProduct(product) || isPackPortions(product)) return null;
  const rows: PortionStockRow[] = [];
  const seen = new Set<string>();
  for (const p of foodPortions(product)) {
    const variantId = portionVariantId(p.name);
    const key = stockVariantKey(variantId);
    if (seen.has(key)) continue;
    seen.add(key);
    rows.push({ variantId, label: p.name });
  }
  if (rows.length === 0) return null;
  if (Number(opts?.unsizedStock) > 0) {
    rows.push({ variantId: UNSIZED_VARIANT_ID, label: UNSIZED_STOCK_LABEL, unsized: true });
  }
  return rows;
}

/** "Big or Small", "Big, Medium or Small" — the sizes, as a choice. */
export function sizeChoiceText(rows: ReadonlyArray<PortionStockRow>): string {
  const names = rows.filter((r) => !r.unsized).map((r) => r.label);
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} or ${names[names.length - 1]}`;
}

/**
 * The server's check on new stock: a menu item sold by size only takes stock
 * that names one of its sizes. Returns the refusal to throw, or null when the
 * write is fine (the right size, or an item that isn't sold by size).
 */
export function unsizedStockRefusal(product: any, variantId: string | null | undefined): string | null {
  const rows = portionStockRows(product);
  if (!rows) return null;
  const key = stockVariantKey(variantId);
  if (rows.some((r) => stockVariantKey(r.variantId) === key)) return null;
  const name = String(product?.name ?? "").trim() || "This item";
  return (
    `Pick a size: ${sizeChoiceText(rows)}. ${name} keeps its own count for each size, ` +
    `and stock saved without one can't be sold at the till.`
  );
}

/**
 * Units per stock key, from a per-variant map (the digest's `variantPos` the
 * till receives as `variantStock`, or `getVariantStockTotals`) or from batch
 * rows. Keys go through `stockVariantKey`, so two spellings of one id add up
 * instead of hiding each other, and a batch with no variant id is unsized.
 */
export function normalizeStockMap(
  source:
    | Record<string, number>
    | ReadonlyArray<{ variantId?: string | null; remainingQuantity?: number }>
    | null
    | undefined,
): Record<string, number> {
  const out: Record<string, number> = {};
  if (!source) return out;
  const add = (variantId: string | null | undefined, qty: unknown) => {
    const n = Number(qty) || 0;
    if (n === 0) return;
    const key = stockVariantKey(variantId);
    out[key] = (out[key] || 0) + n;
  };
  if (Array.isArray(source)) {
    for (const b of source as ReadonlyArray<{ variantId?: string | null; remainingQuantity?: number }>) {
      add(b?.variantId, b?.remainingQuantity);
    }
  } else {
    for (const [variantId, qty] of Object.entries(source as Record<string, number>)) add(variantId, qty);
  }
  return out;
}

/** One size of a menu item, as the till sees it. */
export interface PortionSaleStock {
  /** The portion's name, as the till shows it. */
  name: string;
  variantId: string;
  /** Units of this size on the shelf — 0 when the till has no figure for it. */
  onShelf: number;
  /** Units of this size already in this cart. */
  inCart: number;
  /** What the till may still add: on the shelf less what the cart holds. */
  left: number;
}

/**
 * The till's view of a menu item sold by size: what each size can still sell,
 * from the per-variant stock the till receives (`product.variantStock` —
 * getPosStock, the digest's `variantPos`) less the cart's lines for that size.
 * A size with no figure has none. Null when the item keeps ONE stock (no
 * sizes, or packs) or doesn't track stock — the till then uses the item's
 * total, as before.
 *
 * Stock saved without a size is never counted: the till sends a size with
 * every line, so the server could never sell it (Fitness World's "12 left"
 * on an empty shelf).
 */
export function portionSaleStock(
  product: any,
  cartLines?: ReadonlyArray<{
    productId?: string | null;
    variantId?: string | null;
    size?: string | null;
    quantity?: number | null;
  }> | null,
): PortionSaleStock[] | null {
  if (!isStockTracked(product)) return null;
  const rows = portionStockRows(product);
  if (!rows) return null;
  const shelf = normalizeStockMap(product?.variantStock);
  const productId = product?._id != null ? String(product._id) : "";
  const lines = productId
    ? (cartLines || []).filter((l) => !!l && String(l.productId ?? "") === productId)
    : [];
  return rows.map((r) => {
    const key = stockVariantKey(r.variantId);
    const onShelf = Math.max(0, shelf[key] ?? 0);
    const inCart = lines
      .filter((l) => (l.variantId ? stockVariantKey(l.variantId) === key : l.size === r.label))
      .reduce((n, l) => n + (Number(l.quantity) || 0), 0);
    return { name: r.label, variantId: r.variantId, onShelf, inCart, left: Math.max(0, onShelf - inCart) };
  });
}
